import React from 'react'
import Barcode from 'react-barcode'
import * as dayjs from 'dayjs'

const CheckInSlip = React.forwardRef(({ orderData, bags }, ref) => {
  const order = orderData && orderData[0] ? orderData[0] : null
  const customer = order?.customer && order?.customer[0] ? order.customer[0] : null
  const address = order?.addressId && order?.addressId[0] ? order.addressId[0] : null

  // Calculate total items across all bags
  const totalItemsCount = bags?.reduce((acc, bag) => acc + (bag?.item?.length || 0), 0) || 0

  const orderNumber = order?.orderId ? String(order?.orderId) : 'N/A'
  const currentDate = dayjs().format('DD MMM YYYY, hh:mm A')

  return (
    <div ref={ref} style={{
      width: '100%',
      maxWidth: '380px',
      margin: '0 auto',
      padding: '16px',
      backgroundColor: '#ffffff',
      color: '#000000',
      fontFamily: 'Courier New, monospace, sans-serif',
      fontSize: '12px',
      lineHeight: '1.4',
      border: '1px solid #ddd',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', borderBottom: '2px dashed #000', paddingBottom: '10px', marginBottom: '10px' }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 'bold', letterSpacing: '1px' }}>CUE LAUNDRY</h2>
        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>ORDER CHECK-IN SLIP</div>
        <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>{currentDate}</div>
      </div>

      {/* Barcode & Order Number */}
      <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '8px' }}>
        {order?.orderId && (
          <div style={{ display: 'inline-block' }}>
            <Barcode
              value={String(order.orderId)}
              width={1.6}
              height={40}
              fontSize={13}
              margin={2}
            />
          </div>
        )}
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
          Order ID: #{orderNumber}
        </div>
        <div style={{ fontSize: '11px', textTransform: 'uppercase' }}>
          Type: <b>{order?.orderType || 'Standard'}</b> | Status: <b>CHECKED IN</b>
        </div>
      </div>

      {/* Customer Information */}
      <div style={{ marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '8px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>Customer Details:</div>
        <div><b>Name:</b> {customer?.username || order?.name || 'Walk-in Customer'}</div>
        <div><b>Phone:</b> {customer?.mobile || 'N/A'}</div>
        {address?.address && (
          <div style={{ fontSize: '11px', marginTop: '2px' }}>
            <b>Address:</b> {address.address}
          </div>
        )}
        {order?.customerNotes && (
          <div style={{ marginTop: '4px', padding: '4px', background: '#f5f5f5', borderLeft: '3px solid #000', fontSize: '11px' }}>
            <b>Notes:</b> {order.customerNotes}
          </div>
        )}
      </div>

      {/* Summary Box */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 10px',
        background: '#f0f0f0',
        borderRadius: '4px',
        fontWeight: 'bold',
        marginBottom: '10px',
        border: '1px solid #ccc'
      }}>
        <span>Total Bags: {bags?.length || 0}</span>
        <span>Total Clothes: {totalItemsCount}</span>
      </div>

      {/* Bags & Items Breakdown */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
          Laundry Process Breakdown:
        </div>
        {bags && bags.length > 0 ? (
          bags.map((bag, bIndex) => {
            const serviceName = bag?.service?.serviceName_EN || 'Standard Wash'
            return (
              <div key={bIndex} style={{ marginBottom: '10px', border: '1px solid #eee', padding: '6px', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: '3px' }}>
                  <span>BAG #{bIndex + 1}</span>
                  <span style={{ color: '#2b2b2b' }}>{serviceName}</span>
                </div>
                <div style={{ fontSize: '11px', color: '#666', margin: '3px 0' }}>
                  Items in bag: {bag?.item?.length || 0}
                </div>

                {bag?.item && bag.item.length > 0 ? (
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', marginTop: '4px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #aaa', textAlign: 'left' }}>
                        <th style={{ padding: '2px 0' }}>Tag #</th>
                        <th style={{ padding: '2px 0' }}>Item</th>
                        <th style={{ padding: '2px 0' }}>Brand/Info</th>
                        <th style={{ padding: '2px 0', textAlign: 'right' }}>Stains</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bag.item.map((it, itIndex) => {
                        // Match item name from bag.itemDetails if populated
                        const matchedItem = bag?.itemDetails?.find(
                          (m) => String(m._id) === String(it?.itemId)
                        )
                        const itemName = matchedItem?.itemName_EN || it?.itemName_EN || 'Garment'
                        const stainCount = it?.stain?.length || 0

                        return (
                          <tr key={itIndex} style={{ borderBottom: '1px dotted #ccc' }}>
                            <td style={{ padding: '3px 0', fontWeight: 'bold' }}>{it?.itemCode || `-`}</td>
                            <td style={{ padding: '3px 0' }}>{itemName}</td>
                            <td style={{ padding: '3px 0', color: '#555' }}>{it?.brand || '-'}</td>
                            <td style={{ padding: '3px 0', textAlign: 'right', color: stainCount > 0 ? '#d9534f' : '#888' }}>
                              {stainCount > 0 ? `${stainCount} stain` : 'None'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ fontStyle: 'italic', fontSize: '11px', color: '#888' }}>No items recorded.</div>
                )}
              </div>
            )
          })
        ) : (
          <div style={{ fontStyle: 'italic', color: '#888' }}>No bags available.</div>
        )}
      </div>

      {/* Addon Services (if any) */}
      {order?.addOnServiceDetail && order.addOnServiceDetail.length > 0 && (
        <div style={{ marginBottom: '10px', borderTop: '1px dashed #000', paddingTop: '6px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '11px', textTransform: 'uppercase' }}>Add-on Services:</div>
          {order.addOnServiceDetail.map((addon, aIdx) => (
            <div key={aIdx} style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
              <span>• {addon?.serviceName_EN} (x{addon?.quantity || 1})</span>
              <span>฿{addon?.price}</span>
            </div>
          ))}
        </div>
      )}

      {/* Control Checklist for Staff */}
      <div style={{ borderTop: '2px dashed #000', paddingTop: '8px', marginTop: '10px', fontSize: '11px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', textAlign: 'center' }}>
          LAUNDRY CONTROL CHECKPOINT
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span>1. Checked In By:</span>
          <span>___________________</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span>2. Washing / Drying:</span>
          <span>___________________</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span>3. Ironing / Packing:</span>
          <span>___________________</span>
        </div>
      </div>

      {/* Footer Instructions */}
      <div style={{
        marginTop: '12px',
        paddingTop: '6px',
        borderTop: '1px solid #000',
        textAlign: 'center',
        fontSize: '10px',
        color: '#444'
      }}>
        <div>* KEEP THIS SLIP WITH BAG DURING ALL PROCESSES *</div>
        <div>Thank you for choosing Cue Laundry!</div>
      </div>
    </div>
  )
})

CheckInSlip.displayName = 'CheckInSlip'

export default CheckInSlip
